import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  MessageSquare,
  FolderTree,
  Newspaper,
  Award,
  Briefcase,
  Palette,
  FileCheck,
  Globe,
  FileImage,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  productService,
  inquiryService,
  categoryService,
  newsService,
  jobApplicationService,
  attachmentService,
} from '@/services/storage';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    products: 0,
    inquiries: 0,
    categories: 0,
    news: 0,
    pendingInquiries: 0,
    jobApplications: 0,
    pendingApplications: 0,
    attachments: 0,
  });

  const [recentInquiries, setRecentInquiries] = useState<any[]>([]);

  useEffect(() => {
    const loadStats = async () => {
      const allJobApps = await jobApplicationService.getAll();
      const allInquiries = await inquiryService.getAll();
      setStats({
        products: (await productService.getAll()).length,
        inquiries: allInquiries.length,
        categories: (await categoryService.getAll()).length,
        news: (await newsService.getAll()).length,
        pendingInquiries: allInquiries.filter((i) => i.status === 'pending').length,
        jobApplications: allJobApps.length,
        pendingApplications: allJobApps.filter((a) => a.status === 'pending').length,
        attachments: (await attachmentService.getAll()).length,
      });

      setRecentInquiries(
        allInquiries
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 5)
      );
    };
    loadStats();
  }, []);

  const statCards = [
    {
      title: '产品总数',
      value: stats.products,
      icon: <Package className="h-6 w-6" />,
      link: '/admin/products',
      color: 'bg-blue-500',
    },
    {
      title: '询单总数',
      value: stats.inquiries,
      icon: <MessageSquare className="h-6 w-6" />,
      link: '/admin/inquiries',
      color: 'bg-green-500',
      badge: stats.pendingInquiries > 0 ? `${stats.pendingInquiries} 待处理` : null,
    },
    {
      title: '产品分类',
      value: stats.categories,
      icon: <FolderTree className="h-6 w-6" />,
      link: '/admin/categories',
      color: 'bg-purple-500',
    },
    {
      title: '新闻文章',
      value: stats.news,
      icon: <Newspaper className="h-6 w-6" />,
      link: '/admin/news',
      color: 'bg-orange-500',
    },
    {
      title: '简历投递',
      value: stats.jobApplications,
      icon: <FileCheck className="h-6 w-6" />,
      link: '/admin/job-applications',
      color: 'bg-pink-500',
      badge: stats.pendingApplications > 0 ? `${stats.pendingApplications} 待审核` : null,
    },
    {
      title: '附件数量',
      value: stats.attachments,
      icon: <FileImage className="h-6 w-6" />,
      link: '/admin/attachments',
      color: 'bg-teal-500',
    },
  ];

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">控制台</h1>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => (
          <Link key={card.title} to={card.link}>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{card.title}</p>
                    <p className="text-3xl font-bold">{card.value}</p>
                    {card.badge && (
                      <span className="inline-block mt-2 px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                        {card.badge}
                      </span>
                    )}
                  </div>
                  <div className={`w-12 h-12 ${card.color} rounded-lg flex items-center justify-center text-white`}>
                    {card.icon}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Inquiries */}
      <div className="grid lg:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>最新询单</CardTitle>
          </CardHeader>
          <CardContent>
            {recentInquiries.length > 0 ? (
              <div className="space-y-4">
                {recentInquiries.map((inquiry) => (
                  <div
                    key={inquiry.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{inquiry.name}</p>
                      <p className="text-sm text-gray-500">
                        {inquiry.productModel} - {inquiry.quantity || '未填写数量'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {new Date(inquiry.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        inquiry.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-600'
                          : inquiry.status === 'processing'
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-green-100 text-green-600'
                      }`}
                    >
                      {inquiry.status === 'pending'
                        ? '待处理'
                        : inquiry.status === 'processing'
                        ? '处理中'
                        : '已完成'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">暂无询单</p>
            )}
            <Link
              to="/admin/inquiries"
              className="block text-center mt-4 text-blue-600 hover:underline"
            >
              查看全部询单
            </Link>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card>
          <CardHeader>
            <CardTitle>快速操作</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <Link
                to="/admin/products/new"
                className="p-4 bg-blue-50 rounded-lg text-center hover:bg-blue-100 transition-colors"
              >
                <Package className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <p className="font-medium text-blue-900">添加产品</p>
              </Link>
              <Link
                to="/admin/inquiries"
                className="p-4 bg-green-50 rounded-lg text-center hover:bg-green-100 transition-colors"
              >
                <MessageSquare className="h-8 w-8 mx-auto mb-2 text-green-600" />
                <p className="font-medium text-green-900">查看询单</p>
              </Link>
              <Link
                to="/admin/categories"
                className="p-4 bg-purple-50 rounded-lg text-center hover:bg-purple-100 transition-colors"
              >
                <FolderTree className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                <p className="font-medium text-purple-900">管理分类</p>
              </Link>
              <Link
                to="/admin/honors"
                className="p-4 bg-yellow-50 rounded-lg text-center hover:bg-yellow-100 transition-colors"
              >
                <Award className="h-8 w-8 mx-auto mb-2 text-yellow-600" />
                <p className="font-medium text-yellow-900">荣誉资质</p>
              </Link>
              <Link
                to="/admin/jobs"
                className="p-4 bg-pink-50 rounded-lg text-center hover:bg-pink-100 transition-colors"
              >
                <Briefcase className="h-8 w-8 mx-auto mb-2 text-pink-600" />
                <p className="font-medium text-pink-900">招聘职位</p>
              </Link>
              <Link
                to="/admin/job-applications"
                className="p-4 bg-cyan-50 rounded-lg text-center hover:bg-cyan-100 transition-colors"
              >
                <FileCheck className="h-8 w-8 mx-auto mb-2 text-cyan-600" />
                <p className="font-medium text-cyan-900">简历投递</p>
              </Link>
              <Link
                to="/admin/page-content"
                className="p-4 bg-indigo-50 rounded-lg text-center hover:bg-indigo-100 transition-colors"
              >
                <Palette className="h-8 w-8 mx-auto mb-2 text-indigo-600" />
                <p className="font-medium text-indigo-900">页面内容</p>
              </Link>
              <Link
                to="/admin/system-config"
                className="p-4 bg-gray-50 rounded-lg text-center hover:bg-gray-100 transition-colors"
              >
                <Globe className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                <p className="font-medium text-gray-900">系统配置</p>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
